import React, { useMemo, useRef, useState } from 'react';
import { Paper, Table, Text } from '@mantine/core';
import { formatPriceWithoutCurrency } from 'app/utils/util-functions';
import { RefinedSubTrade, TradedFacilityParams } from 'app/models/traded-facilities-params';
import { paidToOptions, } from 'app/models/dropdown-options';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ErrorNotification } from 'app/views/components/notifications/notification';
import { SignableDocument } from 'app/models/document-params';
import DocumentPreviewModal from '../documents/document-preview-modal';
import { getPossibleActions, onSignDocument } from '../../utils/signable-document-utils';
import WireInstructionDrawer from './wire-instruction-drawer';
import { useUnappliedTransactions } from '../../utils/useUnappliedTransactions';
import { FMDocumentMetadata } from 'app/models/fm-metadata-params';
import { generateSubTradeRowsHtml, getSubTradeLevelFees, applyPrioritySort } from './preview/calculations';
import { getSubTradeSignableDocument, loadFMMetadataForSubTrade } from 'app/state/ducks/signable-document/thunks';
import { useTradePageWireInstructions } from 'app/views/pages/trade-entry/detail-trade/use-trade-page-lookups';

interface PaymentsSummaryProps {
  refinedSubTrade: RefinedSubTrade;
  tradedFacilityWithCalculatedFee?: TradedFacilityParams[];
  fmMetadata?: FMDocumentMetadata;
}


const PaymentsSummary: React.FC<PaymentsSummaryProps> = ({ refinedSubTrade, tradedFacilityWithCalculatedFee = [], fmMetadata }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const facilityAmounts = fmMetadata?.facilityAmounts ?? []

  // Parent already normalises fmMetadata.subTradeFees onto refinedSubTrade.fees,
  // so we read straight from props.
  const subTradeFees = refinedSubTrade?.fees ?? []

  const assignmentFee = subTradeFees.find((fee) => fee.feeType === 'Assignment Fee');
  const wireInstructions = useTradePageWireInstructions()
  const [openPreviewDocument, setOpenPreviewDocument] = useState<boolean>(false);
  const [signableDocument, setSignableDocument] = useState<SignableDocument | null>(null);
  const [currentDocumentType, setCurrentDocumentType] = useState<string>('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null)
  const [selectedEntityAction, setSelectedEntityAction] = useState<"Buyer" | "Seller">("Seller");
  const activeSessionRef = useRef<string | null>(null);

  const hasUnappliedTransactions = useUnappliedTransactions(refinedSubTrade?.masterTrade?.id);

  // Per-currency purchase totals computed from the SAME logic the Funding Memo
  // PDF uses (generateSubTradeRowsHtml) so the two views cannot drift.
  const totalPurchaseAmountTable = useMemo(() => {
    if (!fmMetadata || tradedFacilityWithCalculatedFee.length === 0) return []

    const usesCashlessTemplate =
      fmMetadata.paymentType === 'Cashless' || fmMetadata.paymentType === 'Elevation'

    // Use the exact same source as the PDF
    const calculations = usesCashlessTemplate
      ? applyPrioritySort([
          ...getSubTradeLevelFees(
            fmMetadata.subTradeFees ?? [],
            tradedFacilityWithCalculatedFee[0]?.facilityDetails?.baseCurrency ?? 'USD',
          ),
        ])
      : generateSubTradeRowsHtml(
          fmMetadata.subTradeFees ?? [],
          facilityAmounts,
          fmMetadata.tradedFacilityWithCalculatedFee,
          fmMetadata.targetSettlementDate,
        )

    // Group the calculation rows by currency
    const byCurrency = calculations.reduce<Record<string, number>>((acc, c) => {
      if (typeof c.value !== 'number') return acc
      const currency = c.currency ?? 'USD'
      acc[currency] = (acc[currency] ?? 0) + c.value
      return acc
    }, {})

    // The FM endpoint resolves the receiving party's wire instructions into
    // wireInstructionsData. We require the routing-essential fields to consider
    // it usable: a beneficiary name plus either an account number or IBAN.
    // This avoids reporting "Yes" for partial data that wouldn't actually
    // route a payment.
    const wiData = fmMetadata.wireInstructionsCalculation?.wireInstructionsData
    const receivingPartyHasWI = !!wiData
      && !!wiData.beneficiaryCustomerName
      && (!!wiData.beneficiaryAccountNumber || !!wiData.beneficiaryIban)
    const settlementCurrency = tradedFacilityWithCalculatedFee[0]?.facilityDetails?.baseCurrency ?? 'USD'
    const backendTotal = Number(fmMetadata.wireInstructionsCalculation?.totalAmount ?? 0)
    // Backend convention: positive totalAmount → buyer remits (seller receives),
    // negative → seller remits (buyer receives).
    const receivingPartyIsSeller = backendTotal > 0

    return Object.entries(byCurrency)
      .map(([currency, amount], i) => {
        // Only the settlement currency carries wireInstructionsData from the endpoint
        const wiResolved = currency === settlementCurrency && receivingPartyHasWI
        return {
          id: i,
          currency,
          amount,
          sellerWireInstructions: wiResolved && receivingPartyIsSeller ? 'Yes' : 'No',
          buyerWireInstructions: wiResolved && !receivingPartyIsSeller ? 'Yes' : 'No',
        }
      })
      .sort((a, b) => b.currency.localeCompare(a.currency))
  }, [fmMetadata, tradedFacilityWithCalculatedFee, facilityAmounts])


  const possibleActions = useMemo(() => {
    return getPossibleActions(refinedSubTrade.masterTrade, signableDocument);
  }, [refinedSubTrade.masterTrade, signableDocument]);


  const selectedEntity = selectedEntityAction === 'Buyer' ? refinedSubTrade.sellerEntity : refinedSubTrade.buyerEntity;

  const selectWireInstruction = selectedEntityAction === 'Buyer' ? refinedSubTrade.sellerWireInstructions : refinedSubTrade.buyerWireInstructions

  // now grab only the wire‑instructions that belong to that entity
  const entityWireInstructions = useMemo(
    () =>
      wireInstructions.filter(
        (wi) => wi.entity?.id === selectedEntity?.id
      ),
    [wireInstructions, selectedEntity?.id]
  );

  // ─── only the instructions matching the clicked currency ─
  const filteredWireInstructions = useMemo(
    () =>
      selectedCurrency
        ? entityWireInstructions.filter((wi) => wi.currency === selectedCurrency)
        : [],
    [entityWireInstructions, selectedCurrency]
  );

  const handleSignDocument = async (signBy: "buyer" | "seller" | "both") => {
    const session = activeSessionRef.current;
    const signed = await onSignDocument(signBy, signableDocument, dispatch);
    if (activeSessionRef.current !== session) return;
    if (signed) setSignableDocument(signed);
  };

  const openPreview = async (documentType: string = "FundingMemoDocument") => {
    if (!fmMetadata?.signableDocument) {
      ErrorNotification({
        title: 'Funding Memo Unavailable',
        message: 'Please refresh the FM document on the Documents tab.',
      });
      navigate(`/tradeentry/${refinedSubTrade.masterTrade.id}/documents`);
      return;
    }

    const session = `${refinedSubTrade.id ?? ''}:${documentType}`;
    activeSessionRef.current = session;
    const response = await getSubTradeSignableDocument(refinedSubTrade.id ?? '', documentType);

    if (activeSessionRef.current !== session) return;

    if (!response.success) {
      ErrorNotification({
        title: 'Error',
        message: response.payload || 'Failed to retrieve the Funding Memo.',
      });
      return;
    }

    setSignableDocument(response.payload);
    setCurrentDocumentType(documentType);
    setOpenPreviewDocument(true);
  };

  return (
    <> <WireInstructionDrawer
      open={openDrawer}
      setOpen={setOpenDrawer}
      entity={selectedEntity}
      refinedSubTrade={refinedSubTrade ?? []}
      wireInstructions={filteredWireInstructions ?? []}
      selectWireInstruction={selectWireInstruction[0]?.id ?? ''}
      selectedEntityAction={selectedEntityAction}

    /><div className="panel-header-payment-summury">
        <span className='panel-title-payment-summary-with-type'>
          <Text className="panel-title-payment-summury">Payments Summary </Text>
          <Text className="panel-title-payment-summury" style={{ fontWeight: "400" }}>{refinedSubTrade.masterTrade.paymentType != 'Standard' && ` (${refinedSubTrade.masterTrade.paymentType})`}</Text>
        </span>
        <Text className="panel-title-view-funding-memo" onClick={() => openPreview()}> View Funding Memo</Text>
      </div > <Paper className="payment-summury-overview" shadow="sm">
        <div className="payment-summury-overview-inside">
          <div className="payment-summury-overview-inside-table">
            <div className="payment-summury-overview-inside-table-header">
              <Text className="payment-summury-overview-inside-table-header-text">Total Purchase Amount</Text>
            </div>
            <div className="table-container">
              <Table highlightOnHover striped={false} className="remove-stripe-table" style={{ width: '100% !important' }}>
                <thead style={{ backgroundColor: '#F0EEEE' }}>
                  <tr className="payment-summury-overview-table-header">
                    <th className='dropdown-header'>Currency</th>
                    <th className='amount-header'>Amount</th>
                    <th className='dropdown-header'>Remitted By</th>
                    <th className='dropdown-header'>Wire Instructions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {totalPurchaseAmountTable?.map((e) => {
                    const isBuyer = e.amount > 0
                    const remitter = isBuyer ? 'Buyer' : 'Seller'
                    const wireInstr = isBuyer
                      ? e.sellerWireInstructions
                      : e.buyerWireInstructions
                    return (
                      <tr key={`${e.id}`} className="table-row" onClick={() => console.log('clicked')}>
                        <td className="payment-summury-overview-table-cell">{e.currency ?? ''}</td>
                        <td className="payment-summury-overview-table-cell" style={{ textAlign: 'end' }}>{formatPriceWithoutCurrency(Math.abs(e.amount), 2, 2) ?? ''}</td>
                        <td className="payment-summury-overview-table-cell" >{remitter}</td>
                        <td className="payment-summury-overview-table-cell"
                          style={{ color: wireInstr === 'Yes' ? '#287A26' : '#EE5959', cursor: 'pointer' }} onClick={() => {
                            setSelectedCurrency(e.currency);
                            setSelectedEntityAction(remitter)
                            setOpenDrawer(true);
                          }}>{wireInstr}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          </div>
          <div className="payment-summury-overview-inside-table">
            <div className="payment-summury-overview-inside-table-header">
              <Text className="payment-summury-overview-inside-table-header-text">Assignment Fee</Text>
            </div>
            <div className="table-container">
              <Table highlightOnHover striped={false} className="remove-stripe-table" style={{ width: '100% !important' }}>
                <thead style={{ backgroundColor: '#F0EEEE' }}>
                  <tr className="payment-summury-overview-table-header">
                    <th className='dropdown-header'>Currency</th>
                    <th className='amount-header'>Amount</th>
                    <th className='amount-header'>Seller</th>
                    <th className='amount-header'>Buyer</th>
                    <th className='dropdown-header'>Remitted By</th>
                    <th className='dropdown-header'>Wire Instructions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {assignmentFee && (
                    <tr key={assignmentFee.id} className="table-row" onClick={() => console.log('clicked')}>
                      <td className="payment-summury-overview-table-cell">
                        {assignmentFee.currency ?? ''}
                      </td>
                      <td className="payment-summury-overview-table-cell" style={{ textAlign: 'end' }}>
                        {formatPriceWithoutCurrency(Number(assignmentFee.feeAmount) * 2, 2, 2) ?? ''}
                      </td>
                      <td className="payment-summury-overview-table-cell" style={{ textAlign: 'end' }}>
                        {formatPriceWithoutCurrency(Number(assignmentFee.feeAmount), 2, 2) ?? ''}
                      </td>
                      <td className="payment-summury-overview-table-cell" style={{ textAlign: 'end' }}>
                        {formatPriceWithoutCurrency(Number(assignmentFee.feeAmount), 2, 2) ?? ''}
                      </td>
                      <td className="payment-summury-overview-table-cell">{paidToOptions.find(option => option.value === assignmentFee.paidTo)?.label ?? ''}</td>
                      <td className="payment-summury-overview-table-cell" style={{ color: fmMetadata?.agentWireInstructions != null ? '#287A26' : '#EE5959' }}>{fmMetadata?.agentWireInstructions != null ? 'Yes' : 'No'}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </Paper>
      {openPreviewDocument && signableDocument && (
        <DocumentPreviewModal
          opened={openPreviewDocument}
          onClose={() => {
            activeSessionRef.current = null;
            setOpenPreviewDocument(false);
            setCurrentDocumentType('');
            setSignableDocument(null);
          }}
          onLockToggled={() => {
            // Lock/unlock changes the FM data the panel displays — refetch so
            // the lock banner, button states, and fee values stay in sync.
            if (refinedSubTrade?.id) {
              dispatch(loadFMMetadataForSubTrade(refinedSubTrade.id) as any)
            }
          }}
          documentUrl={signableDocument.url ?? ''}
          previewHtml={signableDocument.previewHtml ?? null}
          filename={signableDocument.fileName ?? "Test.pdf"}
          documentType={currentDocumentType}
          subTrade={refinedSubTrade}
          onAction={handleSignDocument}
          possibleActions={possibleActions}
          hasUnappliedTransactions={hasUnappliedTransactions}
        />
      )}

    </>
  );
};

export default PaymentsSummary;
